import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, ArrowRight, ArrowsClockwise, CheckCircle, CheckSquare, Lightbulb, PencilSimple, Question, Sparkle, Square, Trophy, XCircle } from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { Alert, Animated, Dimensions, Platform, ScrollView, StyleSheet, TextInput, TouchableOpacity, Vibration, View } from 'react-native';
import ConfettiCannon from 'react-native-confetti-cannon';
import { ActivityIndicator, Text } from 'react-native-paper';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200; // Largeur max cohérente avec les autres pages

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber600: '#D97706',
  amber500: '#F59E0B',
  amber400: '#FBBF24',
  amber100: '#FEF3C7',
  amber50: '#FFFBEB',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  emerald700: '#065F46',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  rose700: '#BE123C',
  blue500: '#3B82F6',
  blue100: '#DBEAFE',
  blue700: '#1D4ED8',
  purple600: '#7C3AED',
};

// ✅ Alert adapté web/mobile
const showAlert = (title: string, message: string, buttons?: any[]) => {
  if (isWeb) {
    window.alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message, buttons);
  }
};

interface QuestionType {
  id?: string;
  question: string;
  choices: string[];
  correct_answer?: number;
  correctAnswer?: number;
  answer?: number;
  correct_answers?: number[];
  type?: 'single' | 'multiple';
  explanation?: string;
  skill?: string;
  question_difficulty?: string;
}

interface ShuffledQuestion extends QuestionType {
  shuffledChoices: string[];
  shuffledCorrectAnswers: number[];
  isMultiple: boolean;
}

interface QuizData {
  id: string;
  title: string | null;
  data: any;
  created_at: string | null;
  document_id?: string | null;
}

const shuffleArray = <T,>(array: T[]): { shuffled: T[]; indexMap: number[] } => {
  const shuffled = [...array];
  const indexMap = array.map((_, i) => i);
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    [indexMap[i], indexMap[j]] = [indexMap[j], indexMap[i]];
  }
  
  return { shuffled, indexMap };
};

export default function QuizDetailScreen() {
  const { id } = useLocalSearchParams();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [questions, setQuestions] = useState<ShuffledQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  
  const [hasAnswered, setHasAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [loading, setLoading] = useState(true);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // ✅ État pour générer un nouveau quiz
  const [isGeneratingNew, setIsGeneratingNew] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');

  const confettiRef = useRef<any>(null);
  const generatingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;
  const resultScaleAnim = useRef(new Animated.Value(0)).current;
  const optionAnims = useRef<Animated.Value[]>([]).current;

  useEffect(() => {
    fetchQuiz();
  }, [id]);

  useEffect(() => {
    if (questions.length > 0) {
      const currentChoices = questions[currentIndex]?.shuffledChoices || [];
      while (optionAnims.length < currentChoices.length) {
        optionAnims.push(new Animated.Value(0));
      }
      animateOptionsIn();
    }
  }, [questions, currentIndex]);

  useEffect(() => {
    if (questions.length > 0) {
      Animated.timing(progressAnim, {
        toValue: ((currentIndex + 1) / questions.length) * 100,
        duration: 500,
        useNativeDriver: false,
      }).start();
    }
  }, [currentIndex, questions.length]);

  useEffect(() => {
    if (isFinished && questions.length > 0) {
      const percentage = Math.round((score / questions.length) * 100);
      if (percentage >= 90) {
        setShowConfetti(true);
        setTimeout(() => {
          confettiRef.current?.start();
        }, 300);
      }
    }
  }, [isFinished, score, questions.length]);

  const animateOptionsIn = () => {
    const currentChoices = questions[currentIndex]?.shuffledChoices || [];
    optionAnims.forEach(anim => anim.setValue(0));
    
    const animations = currentChoices.map((_, index) => {
      return Animated.timing(optionAnims[index] || new Animated.Value(0), {
        toValue: 1,
        duration: 300,
        delay: index * 100,
        useNativeDriver: true,
      });
    });
    
    Animated.parallel(animations).start();
  };

  const animateQuestionTransition = (direction: 'next' | 'prev', callback: () => void) => {
    const toValue = direction === 'next' ? -50 : 50;
    
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: toValue,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      callback();
      slideAnim.setValue(direction === 'next' ? 50 : -50);
      
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });
  };

  const animateSelection = () => {
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const animateResult = () => {
    Animated.spring(resultScaleAnim, {
      toValue: 1,
      friction: 4,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  const extractAndShuffleQuestions = (data: any): ShuffledQuestion[] => {
    if (!data) return [];

    try {
      let parsed = data;
      if (typeof data === 'string') {
        parsed = JSON.parse(data);
      }

      let rawQuestions: QuestionType[] = [];
      
      if (parsed.quiz && Array.isArray(parsed.quiz)) {
        rawQuestions = parsed.quiz;
      } else if (Array.isArray(parsed)) {
        rawQuestions = parsed;
      } else if (parsed.questions && Array.isArray(parsed.questions)) {
        rawQuestions = parsed.questions;
      }

      return rawQuestions.map((q) => {
        const choices = q.choices || [];
        const { shuffled, indexMap } = shuffleArray(choices);
        
        let originalCorrectAnswers: number[] = [];
        
        if (q.correct_answers && Array.isArray(q.correct_answers)) {
          originalCorrectAnswers = q.correct_answers;
        } else if (typeof q.correct_answer === 'number') {
          originalCorrectAnswers = [q.correct_answer];
        } else if (typeof q.correctAnswer === 'number') {
          originalCorrectAnswers = [q.correctAnswer];
        } else if (typeof q.answer === 'number') {
          originalCorrectAnswers = [q.answer];
        } else {
          originalCorrectAnswers = [0];
        }
        
        const shuffledCorrectAnswers = originalCorrectAnswers.map(
          originalIndex => indexMap.indexOf(originalIndex)
        );
        
        const isMultiple = q.type === 'multiple' || originalCorrectAnswers.length > 1;
        
        return {
          ...q,
          shuffledChoices: shuffled,
          shuffledCorrectAnswers,
          isMultiple,
        };
      });
    } catch (e) {
      console.error('Error parsing questions:', e);
      return [];
    }
  };

  const fetchQuiz = async () => {
    try {
      const { data, error } = await supabase
        .from('quizzes')
        .select('id, title, data, created_at, document_id')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching quiz:', error);
        return;
      }

      if (data) {
        setQuiz(data);
        setEditedTitle(data.title || '');
        const shuffledQuestions = extractAndShuffleQuestions(data.data);
        setQuestions(shuffledQuestions);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTitle = () => {
    setEditedTitle(quiz?.title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim() || !quiz) return;
    
    setIsSavingTitle(true);
    try {
      const { error } = await supabase
        .from('quizzes')
        .update({ title: editedTitle.trim() })
        .eq('id', quiz.id);

      if (error) throw error;

      setQuiz({ ...quiz, title: editedTitle.trim() });
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Error saving title:', error);
      showAlert('Erreur', 'Impossible de sauvegarder le titre');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedTitle(quiz?.title || '');
    setIsEditingTitle(false);
  };

  const handleSelectAnswer = (index: number) => {
    if (hasAnswered) return;
    
    const currentQuestion = questions[currentIndex];
    
    if (currentQuestion.isMultiple) {
      setSelectedAnswers(prev => {
        if (prev.includes(index)) {
          return prev.filter(i => i !== index);
        } else {
          return [...prev, index];
        }
      });
    } else {
      setSelectedAnswers([index]);
    }
    
    animateSelection();
    // ✅ Vibration seulement sur mobile
    if (!isWeb) {
      Vibration.vibrate(10);
    }
  };

  const handleAnswer = () => {
    if (selectedAnswers.length === 0) return;
    
    setHasAnswered(true);
    const currentQuestion = questions[currentIndex];
    const correctAnswers = currentQuestion.shuffledCorrectAnswers;
    
    if (currentQuestion.isMultiple) {
      const correctSelected = selectedAnswers.filter(a => correctAnswers.includes(a)).length;
      const incorrectSelected = selectedAnswers.filter(a => !correctAnswers.includes(a)).length;
      const totalCorrect = correctAnswers.length;
      
      if (correctSelected === totalCorrect && incorrectSelected === 0) {
        setScore(score + 1);
        if (!isWeb) Vibration.vibrate([0, 50, 50, 50]);
      } else if (correctSelected > 0 && incorrectSelected === 0) {
        setScore(score + 0.5);
        if (!isWeb) Vibration.vibrate([0, 30, 30, 30]);
      } else {
        if (!isWeb) Vibration.vibrate(200);
      }
    } else {
      if (selectedAnswers.length === 1 && correctAnswers.includes(selectedAnswers[0])) {
        setScore(score + 1);
        if (!isWeb) Vibration.vibrate([0, 50, 50, 50]);
      } else {
        if (!isWeb) Vibration.vibrate(200);
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      animateQuestionTransition('next', () => {
        setCurrentIndex(currentIndex + 1);
        setSelectedAnswers([]);
        setHasAnswered(false);
      });
    } else {
      setIsFinished(true);
      animateResult();
      // Sauvegarder le score
      saveQuizResult();
    }
  };

  // ✅ Sauvegarder le résultat du quiz
  const saveQuizResult = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !quiz) return;

      await supabase.from('quiz_results').insert({
        user_id: user.id,
        quiz_id: quiz.id,
        score: Math.round(score),
        total: questions.length,
      });
    } catch (error) {
      console.error('Error saving quiz result:', error);
    }
  };

  const handleRestart = () => {
    if (quiz) {
      const reshuffledQuestions = extractAndShuffleQuestions(quiz.data);
      setQuestions(reshuffledQuestions);
    }
    
    resultScaleAnim.setValue(0);
    progressAnim.setValue(0);
    setCurrentIndex(0);
    setSelectedAnswers([]);
    setHasAnswered(false);
    setScore(0);
    setIsFinished(false);
    setShowConfetti(false);
  };

  // ✅ Phrases de chargement rotatives
  const generatingMessages = [
    'Préparation de nouvelles questions...',
    'Ernest analyse ton chapitre...',
    'Encore quelques secondes...',
    'C\'est presque prêt !',
  ];

  const startGeneratingMessages = () => {
    let index = 0;
    setGeneratingMessage(generatingMessages[0]);
    
    generatingIntervalRef.current = setInterval(() => {
      index = (index + 1) % generatingMessages.length;
      setGeneratingMessage(generatingMessages[index]);
    }, 3000);
  };

  const stopGeneratingMessages = () => {
    if (generatingIntervalRef.current) {
      clearInterval(generatingIntervalRef.current);
      generatingIntervalRef.current = null;
    }
    setGeneratingMessage('');
  };

  // ✅ Générer un nouveau quiz depuis le même document
  const handleGenerateNewQuiz = async () => {
    if (!quiz?.document_id) {
      showAlert(
        'Non disponible',
        'Ce quiz n\'a pas de document source associé. Crée un nouveau quiz depuis la page Réviser.'
      );
      return;
    }

    setIsGeneratingNew(true);
    startGeneratingMessages();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showAlert('Erreur', 'Non authentifié');
        return;
      }

      const generateResponse = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/generate-from-document`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            document_id: quiz.document_id,
            intent: 'quiz',
          }),
        }
      );

      const generateText = await generateResponse.text();
      let generateResult;
      
      try {
        generateResult = JSON.parse(generateText);
      } catch {
        throw new Error('Erreur lors de la génération');
      }

      if (!generateResponse.ok || generateResult.error) {
        throw new Error(generateResult.error || 'Erreur génération');
      }

      if (generateResult.quiz_id) {
        // Naviguer vers le nouveau quiz
        router.replace(`/quiz/${generateResult.quiz_id}`);
      }
    } catch (error: any) {
      console.error('Error generating new quiz:', error);
      showAlert(
        'Erreur',
        error.message || 'Impossible de générer un nouveau quiz'
      );
    } finally {
      stopGeneratingMessages();
      setIsGeneratingNew(false);
    }
  };

  const getOptionStyle = (index: number) => {
    const currentQuestion = questions[currentIndex];
    const correctAnswers = currentQuestion.shuffledCorrectAnswers;
    const isSelected = selectedAnswers.includes(index);
    const isCorrect = correctAnswers.includes(index);
    
    if (!hasAnswered) {
      return isSelected ? styles.optionSelected : styles.option;
    }
    
    if (isCorrect) {
      return styles.optionCorrect;
    }
    
    if (isSelected && !isCorrect) {
      return styles.optionWrong;
    }
    
    return styles.optionDisabled;
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.amber500} />
        <Text style={styles.loadingText}>Chargement du quiz...</Text>
      </View>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          <LinearGradient
            colors={[colors.amber500, colors.amber600]}
            style={styles.headerGradient}
          >
            {/* ✅ Inner container pour le header */}
            <View style={styles.headerInner}>
              <View style={styles.headerContent}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Quiz</Text>
                <View style={{ width: 40 }} />
              </View>
            </View>
          </LinearGradient>
          <View style={[styles.centered, { flex: 1 }]}>
            <LinearGradient
              colors={[colors.amber100, colors.amber50]}
              style={styles.emptyIconContainer}
            >
              <Question size={48} color={colors.amber500} weight="duotone" />
            </LinearGradient>
            <Text style={styles.emptyTitle}>Quiz introuvable</Text>
            <Text style={styles.emptySubtitle}>Ce quiz n'existe pas ou n'a pas de questions.</Text>
            <TouchableOpacity style={styles.emptyButton} onPress={() => router.back()}>
              <Text style={styles.emptyButtonText}>Retour</Text>
            </TouchableOpacity>
          </View>
        </View>
      </>
    );
  }

  // Écran de résultat
  if (isFinished) {
    const percentage = Math.round((score / questions.length) * 100);
    const getResultData = () => {
      if (percentage >= 80) return { icon: Trophy, message: 'Excellent travail !', color: colors.emerald500, bgColor: colors.emerald100 };
      if (percentage >= 60) return { icon: CheckCircle, message: 'Bien joué !', color: colors.amber500, bgColor: colors.amber100 };
      if (percentage >= 40) return { icon: Lightbulb, message: 'Continue comme ça !', color: colors.blue500, bgColor: colors.blue100 };
      return { icon: ArrowsClockwise, message: 'Révise encore un peu !', color: colors.rose500, bgColor: colors.rose100 };
    };
    const resultData = getResultData();
    const ResultIcon = resultData.icon;

    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          <LinearGradient
            colors={[resultData.color, resultData.color]}
            style={styles.headerGradient}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerContent}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Résultat</Text>
                <View style={{ width: 40 }} />
              </View>
            </View>
          </LinearGradient>
          
          <ScrollView 
            style={{ flex: 1 }} 
            contentContainerStyle={styles.resultScrollContent}
            showsVerticalScrollIndicator={false}
          >
            <Animated.View style={[
              styles.resultCard,
              { transform: [{ scale: resultScaleAnim }] }
            ]}>
              <LinearGradient
                colors={[resultData.bgColor, resultData.color + '20']}
                style={styles.resultIconContainer}
              >
                <ResultIcon size={56} color={resultData.color} weight="duotone" />
              </LinearGradient>
              
              <Text style={styles.resultTitle}>{resultData.message}</Text>
              
              <View style={styles.scoreContainer}>
                <Text style={[styles.resultScore, { color: resultData.color }]}>
                  {Number.isInteger(score) ? score : score.toFixed(1)}
                </Text>
                <Text style={styles.resultScoreDivider}>/</Text>
                <Text style={styles.resultScoreTotal}>{questions.length}</Text>
              </View>
              
              <View style={[styles.percentageBadge, { backgroundColor: resultData.color }]}>
                <Text style={styles.percentageText}>{percentage}%</Text>
              </View>
              
              <View style={styles.resultButtons}>
                {/* ✅ Bouton Nouveau quiz même chapitre */}
                {quiz.document_id && (
                  <TouchableOpacity 
                    style={styles.primaryButton}
                    onPress={handleGenerateNewQuiz}
                    activeOpacity={0.8}
                    disabled={isGeneratingNew}
                  >
                    <LinearGradient
                      colors={isGeneratingNew ? [colors.slate400, colors.slate500] : [colors.indigo600, colors.purple600]}
                      style={styles.primaryButtonGradient}
                    >
                      {isGeneratingNew ? (
                        <View style={styles.generatingContainer}>
                          <ActivityIndicator size="small" color={colors.white} />
                          <Text style={styles.generatingText}>{generatingMessage}</Text>
                        </View>
                      ) : (
                        <>
                          <Sparkle size={20} color={colors.white} weight="fill" />
                          <Text style={styles.primaryButtonText}>Nouveau quiz même chapitre</Text>
                        </>
                      )}
                    </LinearGradient>
                  </TouchableOpacity>
                )}

                <TouchableOpacity 
                  style={styles.primaryButton}
                  onPress={handleRestart}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[resultData.color, resultData.color]}
                    style={styles.primaryButtonGradient}
                  >
                    <ArrowsClockwise size={20} color={colors.white} weight="bold" />
                    <Text style={styles.primaryButtonText}>Recommencer ce quiz</Text>
                  </LinearGradient>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={styles.secondaryButton}
                  onPress={() => router.back()}
                  activeOpacity={0.8}
                >
                  <ArrowLeft size={18} color={colors.slate600} weight="bold" />
                  <Text style={styles.secondaryButtonText}>Retour aux révisions</Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
          </ScrollView>

          {showConfetti && (
            <ConfettiCannon
              ref={confettiRef}
              count={150}
              origin={{ x: SCREEN_WIDTH / 2, y: -20 }}
              autoStart={false}
              fadeOut={true}
              fallSpeed={3000}
              explosionSpeed={350}
              colors={[
                colors.amber400,
                colors.emerald500,
                colors.indigo500,
                colors.purple600,
                colors.rose500,
                '#FFD700',
                '#FF69B4',
              ]}
            />
          )}
        </View>
      </>
    );
  }

  const currentQuestion = questions[currentIndex];
  const choices = currentQuestion.shuffledChoices || [];
  const correctAnswers = currentQuestion.shuffledCorrectAnswers;

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        <LinearGradient
          colors={[colors.amber500, colors.amber600]}
          style={styles.headerGradient}
        >
          <View style={styles.headerInner}>
            <View style={styles.headerContent}>
              <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                <ArrowLeft size={24} color={colors.white} weight="bold" />
              </TouchableOpacity>
              <Text style={styles.headerTitle} numberOfLines={1}>
                {quiz.title || 'Quiz'}
              </Text>
              <View style={{ width: 40 }} />
            </View>
          </View>
        </LinearGradient>

        {/* ✅ ScrollView avec centrage */}
        <ScrollView 
          style={styles.scrollView} 
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContentOuter}
        >
          {/* ✅ Inner container pour limiter la largeur */}
          <View style={styles.innerContainer}>
            <View style={styles.titleHeader}>
              {isEditingTitle ? (
                <View style={styles.editTitleContainer}>
                  <TextInput
                    style={styles.editTitleInput}
                    value={editedTitle}
                    onChangeText={setEditedTitle}
                    placeholder="Titre du quiz"
                    autoFocus={!isWeb}
                    selectTextOnFocus
                  />
                  <View style={styles.editTitleActions}>
                    <TouchableOpacity style={styles.cancelButton} onPress={handleCancelEdit}>
                      <Text style={styles.cancelButtonText}>Annuler</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={[styles.saveButton, !editedTitle.trim() && styles.saveButtonDisabled]} 
                      onPress={handleSaveTitle}
                      disabled={!editedTitle.trim() || isSavingTitle}
                    >
                      {isSavingTitle ? (
                        <ActivityIndicator size="small" color={colors.white} />
                      ) : (
                        <Text style={styles.saveButtonText}>OK</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity onPress={handleEditTitle} style={styles.titleContainer}>
                  <Text style={styles.titleText} numberOfLines={1}>{quiz.title || 'Sans titre'}</Text>
                  <PencilSimple size={16} color={colors.slate400} weight="duotone" />
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.progressContainer}>
              <View style={styles.progressHeader}>
                <Text style={styles.progressLabel}>Question {currentIndex + 1}/{questions.length}</Text>
                <View style={styles.scoreBadge}>
                  <CheckCircle size={16} color={colors.amber500} weight="duotone" />
                  <Text style={styles.scoreLabel}>
                    {Number.isInteger(score) ? score : score.toFixed(1)}
                  </Text>
                </View>
              </View>
              <View style={styles.progressBar}>
                <Animated.View 
                  style={[
                    styles.progressFill,
                    { 
                      width: progressAnim.interpolate({
                        inputRange: [0, 100],
                        outputRange: ['0%', '100%'],
                      })
                    }
                  ]} 
                />
              </View>
            </View>

            <View style={styles.scrollContent}>
              <Animated.View style={[
                styles.questionCard,
                {
                  opacity: fadeAnim,
                  transform: [{ translateX: slideAnim }],
                }
              ]}>
                <View style={styles.questionBadges}>
                  {currentQuestion.skill && (
                    <View style={styles.skillBadge}>
                      <Text style={styles.skillText}>{currentQuestion.skill}</Text>
                    </View>
                  )}
                  {currentQuestion.isMultiple && (
                    <View style={styles.multipleBadge}>
                      <CheckSquare size={14} color={colors.purple600} weight="bold" />
                      <Text style={styles.multipleText}>Plusieurs réponses</Text>
                    </View>
                  )}
                </View>
                <Text style={styles.questionText}>{currentQuestion.question}</Text>
              </Animated.View>

              <Animated.View style={[
                styles.optionsContainer,
                {
                  opacity: fadeAnim,
                  transform: [{ translateX: slideAnim }],
                }
              ]}>
                {choices.map((choice, index) => {
                  const optionAnim = optionAnims[index] || new Animated.Value(1);
                  const isSelected = selectedAnswers.includes(index);
                  const isCorrect = correctAnswers.includes(index);
                  
                  return (
                    <Animated.View
                      key={index}
                      style={{
                        opacity: optionAnim,
                        transform: [{
                          translateY: optionAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [20, 0],
                          })
                        }]
                      }}
                    >
                      <TouchableOpacity
                        style={getOptionStyle(index)}
                        onPress={() => handleSelectAnswer(index)}
                        activeOpacity={hasAnswered ? 1 : 0.7}
                        disabled={hasAnswered}
                      >
                        <View style={styles.optionContent}>
                          <View style={[
                            currentQuestion.isMultiple ? styles.optionCheckbox : styles.optionLetter,
                            isSelected && !hasAnswered && (currentQuestion.isMultiple ? styles.optionCheckboxSelected : styles.optionLetterSelected),
                            hasAnswered && isCorrect && (currentQuestion.isMultiple ? styles.optionCheckboxCorrect : styles.optionLetterCorrect),
                            hasAnswered && isSelected && !isCorrect && (currentQuestion.isMultiple ? styles.optionCheckboxWrong : styles.optionLetterWrong),
                          ]}>
                            {currentQuestion.isMultiple ? (
                              isSelected || (hasAnswered && isCorrect) ? (
                                <CheckSquare 
                                  size={20} 
                                  color={colors.white} 
                                  weight="bold" 
                                />
                              ) : (
                                <Square 
                                  size={20} 
                                  color={hasAnswered ? colors.slate400 : colors.slate500} 
                                  weight="regular" 
                                />
                              )
                            ) : (
                              <Text style={[
                                styles.optionLetterText,
                                (isSelected && !hasAnswered) && styles.optionLetterTextWhite,
                                (hasAnswered && isCorrect) && styles.optionLetterTextWhite,
                                (hasAnswered && isSelected && !isCorrect) && styles.optionLetterTextWhite,
                              ]}>
                                {String.fromCharCode(65 + index)}
                              </Text>
                            )}
                          </View>
                          <Text style={[
                            styles.optionText,
                            hasAnswered && isCorrect && styles.optionTextCorrect,
                            hasAnswered && isSelected && !isCorrect && styles.optionTextWrong,
                          ]}>
                            {choice}
                          </Text>
                          {hasAnswered && isCorrect && (
                            <CheckCircle size={24} color={colors.emerald500} weight="fill" />
                          )}
                          {hasAnswered && isSelected && !isCorrect && (
                            <XCircle size={24} color={colors.rose500} weight="fill" />
                          )}
                        </View>
                      </TouchableOpacity>
                    </Animated.View>
                  );
                })}
              </Animated.View>

              {hasAnswered && currentQuestion.explanation && (
                <Animated.View style={[styles.explanationCard, { opacity: fadeAnim }]}>
                  <View style={styles.explanationHeader}>
                    <Lightbulb size={20} color={colors.indigo600} weight="duotone" />
                    <Text style={styles.explanationTitle}>Explication</Text>
                  </View>
                  <Text style={styles.explanationText}>{currentQuestion.explanation}</Text>
                </Animated.View>
              )}

              <View style={{ height: 120 }} />
            </View>
          </View>
        </ScrollView>

        {/* ✅ Bottom bar avec centrage */}
        <View style={styles.bottomBar}>
          <View style={styles.bottomBarInner}>
            {!hasAnswered ? (
              <TouchableOpacity
                style={[
                  styles.actionButton,
                  selectedAnswers.length === 0 && styles.actionButtonDisabled
                ]}
                onPress={handleAnswer}
                disabled={selectedAnswers.length === 0}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={selectedAnswers.length === 0 ? [colors.slate300, colors.slate300] : [colors.amber500, colors.amber600]}
                  style={styles.actionButtonGradient}
                >
                  <Text style={styles.actionButtonText}>Valider ma réponse</Text>
                </LinearGradient>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleNext}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={[colors.emerald500, colors.emerald600]}
                  style={styles.actionButtonGradient}
                >
                  <Text style={styles.actionButtonText}>
                    {currentIndex < questions.length - 1 ? 'Question suivante' : 'Voir le résultat'}
                  </Text>
                  <ArrowRight size={20} color={colors.white} weight="bold" />
                </LinearGradient>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 15,
  },

  headerGradient: {
    paddingTop: isWeb ? 24 : 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    // ✅ Centrage du contenu header
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Inner container pour le header
  headerInner: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  
  // ✅ Inner container pour le contenu
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      alignSelf: 'center',
    }),
  },
  
  titleHeader: {
    backgroundColor: colors.white,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate200,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  titleText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
  },
  editTitleContainer: {
    gap: 10,
  },
  editTitleInput: {
    backgroundColor: colors.slate50,
    borderWidth: 2,
    borderColor: colors.amber500,
    borderRadius: 12,
    padding: 12,
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    textAlign: 'center',
  },
  editTitleActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: colors.slate100,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate500,
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: colors.amber500,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    backgroundColor: colors.slate300,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },

  progressContainer: {
    padding: 16,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate200,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  progressLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate900,
  },
  scoreBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.amber100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  scoreLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.amber500,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.slate200,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.amber500,
    borderRadius: 4,
  },

  scrollView: {
    flex: 1,
  },
  // ✅ Centrage du scroll content
  scrollContentOuter: {
    flexGrow: 1,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  scrollContent: {
    padding: 16,
  },
  questionCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 24,
    marginBottom: 20,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  questionBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  skillBadge: {
    backgroundColor: colors.amber100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  skillText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.amber600,
    textTransform: 'capitalize',
  },
  multipleBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.indigo100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  multipleText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.purple600,
  },
  questionText: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    lineHeight: 28,
  },

  optionsContainer: {
    gap: 12,
  },
  option: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.slate200,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  optionSelected: {
    backgroundColor: colors.amber50,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.amber500,
    shadowColor: colors.amber500,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  optionCorrect: {
    backgroundColor: colors.emerald100,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.emerald500,
  },
  optionWrong: {
    backgroundColor: colors.rose100,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.rose500,
  },
  optionDisabled: {
    backgroundColor: colors.slate50,
    borderRadius: 16,
    padding: 18,
    borderWidth: 2,
    borderColor: colors.slate200,
    opacity: 0.6,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  optionLetter: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  optionLetterSelected: {
    backgroundColor: colors.amber500,
  },
  optionLetterCorrect: {
    backgroundColor: colors.emerald500,
  },
  optionLetterWrong: {
    backgroundColor: colors.rose500,
  },
  optionLetterText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.slate500,
  },
  optionLetterTextWhite: {
    color: colors.white,
  },
  optionCheckbox: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    borderWidth: 2,
    borderColor: colors.slate300,
  },
  optionCheckboxSelected: {
    backgroundColor: colors.amber500,
    borderColor: colors.amber500,
  },
  optionCheckboxCorrect: {
    backgroundColor: colors.emerald500,
    borderColor: colors.emerald500,
  },
  optionCheckboxWrong: {
    backgroundColor: colors.rose500,
    borderColor: colors.rose500,
  },
  optionText: {
    flex: 1,
    fontSize: 16,
    color: colors.slate900,
    lineHeight: 24,
  },
  optionTextCorrect: {
    color: colors.emerald700,
    fontWeight: '600',
  },
  optionTextWrong: {
    color: colors.rose700,
  },

  explanationCard: {
    backgroundColor: colors.indigo50,
    borderRadius: 16,
    padding: 20,
    marginTop: 20,
    borderLeftWidth: 4,
    borderLeftColor: colors.indigo600,
  },
  explanationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  explanationTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.indigo700,
  },
  explanationText: {
    fontSize: 15,
    color: colors.indigo700,
    lineHeight: 24,
  },

  bottomBar: {
    padding: 16,
    paddingBottom: isWeb ? 16 : 34,
    backgroundColor: colors.white,
    borderTopWidth: 1,
    borderTopColor: colors.slate200,
    // ✅ Centrage du contenu
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Inner container pour bottom bar
  bottomBarInner: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },
  actionButton: {
    borderRadius: 16,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  actionButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    gap: 8,
  },
  actionButtonDisabled: {
    opacity: 1,
  },
  actionButtonText: {
    color: colors.white,
    fontSize: 17,
    fontWeight: '700',
  },

  emptyIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  emptyButton: {
    marginTop: 24,
    backgroundColor: colors.amber500,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  emptyButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },

  // ✅ Styles pour l'écran de résultat avec scroll
  resultScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  resultCard: {
    backgroundColor: colors.white,
    borderRadius: 28,
    padding: 32,
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 24,
    elevation: 8,
  },
  resultIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  resultTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 20,
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 16,
  },
  resultScore: {
    fontSize: 64,
    fontWeight: '800',
  },
  resultScoreDivider: {
    fontSize: 32,
    fontWeight: '300',
    color: colors.slate400,
    marginHorizontal: 8,
  },
  resultScoreTotal: {
    fontSize: 32,
    fontWeight: '600',
    color: colors.slate500,
  },
  percentageBadge: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 20,
    marginBottom: 28,
  },
  percentageText: {
    color: colors.white,
    fontSize: 18,
    fontWeight: '700',
  },
  resultButtons: {
    width: '100%',
    gap: 12,
  },
  primaryButton: {
    borderRadius: 16,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  primaryButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 8,
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },
  // ✅ Style pour le chargement avec message
  generatingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  generatingText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
    padding: 16,
    backgroundColor: colors.slate100,
    gap: 8,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  secondaryButtonText: {
    color: colors.slate600,
    fontSize: 16,
    fontWeight: '600',
  },
});
